"use client"

import { useState, useEffect } from "react"
import axios from "../api/axios"
import { toast } from "react-toastify"
import {
  Upload,
  File,
  X,
  Edit2,
  Check,
  AlertCircle,
  FileText,
  ImageIcon,
  Video,
  Music,
  Archive,
  Code,
} from "lucide-react"

function UploadPage() {
  const [files, setFiles] = useState([])
  const [fileType, setFileTypes] = useState([])
  const [selectedFileType, setSelectedFileType] = useState("")
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [editingIndex, setEditingIndex] = useState(-1)
  const [newFileName, setNewFileName] = useState("")

  useEffect(() => {
    fetchFileType()
  }, [])

  const fetchFileType = async () => {
    try {
      const res = await axios.get("/api/file-type")
      setFileTypes(res.data)
    } catch (err) {
      console.error("Failed to fetch file type", err)
      toast.error("Failed to fetch file type")
    }
  }

  const getFileTypeIcon = (typeName) => {
    const name = typeName.toLowerCase()
    if (name.includes("image") || name.includes("photo")) return ImageIcon
    if (name.includes("video")) return Video
    if (name.includes("audio") || name.includes("music")) return Music
    if (name.includes("archive") || name.includes("zip")) return Archive
    if (name.includes("code") || name.includes("script")) return Code
    return FileText
  }

  const getFileTypeColor = (typeName) => {
    const name = typeName.toLowerCase()
    if (name.includes("image") || name.includes("photo")) return "bg-green-100 text-green-700 border-green-200"
    if (name.includes("video")) return "bg-purple-100 text-purple-700 border-purple-200"
    if (name.includes("audio") || name.includes("music")) return "bg-blue-100 text-blue-700 border-blue-200"
    if (name.includes("archive") || name.includes("zip")) return "bg-yellow-100 text-yellow-700 border-yellow-200"
    if (name.includes("code") || name.includes("script")) return "bg-gray-100 text-gray-700 border-gray-200"
    return "bg-orange-100 text-orange-700 border-orange-200"
  }

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files)
    const filesWithNames = newFiles.map((file) => ({
      file,
      originalName: file.name,
      displayName: file.name,
      size: file.size,
    }))
    setSelectedFiles((prev) => [...prev, ...filesWithNames])
  }

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const startEditing = (index) => {
    setEditingIndex(index)
    setNewFileName(selectedFiles[index].displayName)
  }

  const saveFileName = () => {
    if (newFileName.trim()) {
      setSelectedFiles((prev) =>
        prev.map((file, i) => (i === editingIndex ? { ...file, displayName: newFileName.trim() } : file)),
      )
    }
    setEditingIndex(-1)
    setNewFileName("")
  }

  const cancelEditing = () => {
    setEditingIndex(-1)
    setNewFileName("")
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.error("Please select at least one file")
      return
    }

    if (!selectedFileType) {
      toast.error("Please select a file type")
      return
    }

    console.log("Uploading with file_type_id:", selectedFileType)
    setIsUploading(true)
    const formData = new FormData()

    selectedFiles.forEach((fileObj) => {
      formData.append("files[]", fileObj.file)
    })

    formData.append("file_type_id", selectedFileType)

    // Debug: Log what we're sending
    console.log("FormData contents:")
    for (const [key, value] of formData.entries()) {
      console.log(key, value)
    }

    try {
      await axios.get("/sanctum/csrf-cookie")
      const res = await axios.post("/api/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      console.log("Upload response:", res.data)
      toast.success("Files uploaded successfully!")
      setSelectedFiles([])
      setSelectedFileType("")
    } catch (err) {
      console.error("Upload error:", err.response?.data)
      toast.error("Upload failed")
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl mb-4">
            <Upload size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Files</h1>
          <p className="text-gray-600">Select files and organize them by type for easy management</p>
        </div>

        {/* File Type Selection */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <FileText size={20} className="mr-2 text-orange-600" />
            Select File Type
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {fileType.map((type) => {
              const IconComponent = getFileTypeIcon(type.name)
              const colorClass = getFileTypeColor(type.name)
              const isSelected = selectedFileType === type.id.toString()

              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedFileType(type.id.toString())}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                    isSelected
                      ? "border-orange-500 bg-orange-50 shadow-md transform scale-105"
                      : `${colorClass} hover:shadow-md hover:scale-102 border-2`
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${isSelected ? "bg-orange-100" : "bg-white"}`}>
                      <IconComponent size={20} className={isSelected ? "text-orange-600" : ""} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isSelected ? "text-orange-900" : ""}`}>{type.name}</h3>
                      <p className={`text-sm ${isSelected ? "text-orange-700" : "text-gray-600"}`}>
                        {type.description || "Click to select"}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="mt-2 flex justify-end">
                      <Check size={16} className="text-orange-600" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* File Selection */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <File size={20} className="mr-2 text-orange-600" />
            Select Files
            {selectedFiles.length > 0 && (
              <span className="ml-2 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-medium">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected
              </span>
            )}
          </h2>

          {/* File Input */}
          <div className="mb-6">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-orange-300 rounded-xl cursor-pointer bg-orange-50 hover:bg-orange-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload size={32} className="text-orange-500 mb-2" />
                <p className="text-sm text-orange-700 font-medium">Click to select files</p>
                <p className="text-xs text-orange-600">or drag and drop files here</p>
              </div>
              <input type="file" multiple onChange={handleFileSelect} className="hidden" />
            </label>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Selected Files:</h3>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {selectedFiles.map((fileObj, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-3 flex-1">
                      <File size={16} className="text-gray-500" />
                      <div className="flex-1">
                        {editingIndex === index ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={newFileName}
                              onChange={(e) => setNewFileName(e.target.value)}
                              className="flex-1 px-2 py-1 border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                              onKeyPress={(e) => e.key === "Enter" && saveFileName()}
                            />
                            <button onClick={saveFileName} className="p-1 text-green-600 hover:bg-green-100 rounded">
                              <Check size={16} />
                            </button>
                            <button onClick={cancelEditing} className="p-1 text-red-600 hover:bg-red-100 rounded">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-gray-900">{fileObj.displayName}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(fileObj.size)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(index)}
                        className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                        title="Rename file"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Remove file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="text-center">
          {!selectedFileType && selectedFiles.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-center space-x-2">
              <AlertCircle size={16} className="text-yellow-600" />
              <span className="text-yellow-700 text-sm">Please select a file type before uploading</span>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!selectedFiles.length || !selectedFileType || isUploading}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto shadow-lg"
          >
            {isUploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload size={20} />
                <span>
                  Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? "s" : ""}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadPage
